// src/components/Dashboard/charts/FoldersByMonthChart.tsx
import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";
import { Empty, Checkbox, Space, Typography } from "antd";

const { Text } = Typography;

interface FoldersByMonthChartProps {
  data: any[];
  period: string;
  filterType?: "preset" | "custom";
  dateRange?: [string, string] | null;
}

interface DataSeries {
  name: string;
  key: string;
  color: string;
  visible: boolean;
}

const FoldersByMonthChart: React.FC<FoldersByMonthChartProps> = ({
  data,
  period,
  filterType = "preset",
  dateRange = null,
}) => {
  // Define data series with visibility state
  const [dataSeries, setDataSeries] = useState<DataSeries[]>([
    { name: "Folders Totales", key: "total", color: "#3b82f6", visible: true },
    { name: "Folders Activos", key: "active", color: "#10b981", visible: true },
    {
      name: "Folders Completados",
      key: "completed",
      color: "#5b08ba",
      visible: true,
    },
    {
      name: "Folders Inactivos",
      key: "inactive",
      color: "#ef4444",
      visible: true,
    },
  ]);

  if (!data || data.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Empty description="No data available for the selected period" />
      </div>
    );
  }

  // Format the data for the chart
  const formattedData = data.map((item) => ({
    ...item,
    name: format(new Date(item.month), "MMM yyyy"),
    total: item.total || 0,
    active: item.active || 0,
    completed: item.completed || 0,
    inactive: item.inactive || 0,
  }));

  // Handle toggle visibility of a data series
  const toggleSeriesVisibility = (key: string) => {
    setDataSeries(
      dataSeries.map((series) =>
        series.key === key ? { ...series, visible: !series.visible } : series,
      ),
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium text-gray-600">{label}</p>
          {payload
            .filter(
              (entry: any) =>
                dataSeries.find((series) => series.key === entry.dataKey)
                  ?.visible,
            )
            .map((entry: any, index: number) => (
              <p key={index} style={{ color: entry.color }}>
                {entry.name}: <span className="font-bold">{entry.value}</span>{" "}
                folders
              </p>
            ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {/* Toggle controls */}
      <div className="mb-4 flex flex-wrap">
        <Space size="middle" wrap>
          {dataSeries.map((series) => (
            <Checkbox
              key={series.key}
              checked={series.visible}
              onChange={() => toggleSeriesVisibility(series.key)}
            >
              <span style={{ color: series.color }}>{series.name}</span>
            </Checkbox>
          ))}
        </Space>
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer>
          <LineChart
            data={formattedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="name"
              tick={{ fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              onClick={(e) => {
                const key = e.dataKey as string;
                toggleSeriesVisibility(key);
              }}
            />

            {/* Dynamically render visible lines */}
            {dataSeries.map(
              (series) =>
                series.visible && (
                  <Line
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.name}
                    stroke={series.color}
                    strokeWidth={2}
                    activeDot={{ r: 6 }}
                    dot={{ r: 3 }}
                  />
                ),
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Display current date range */}
      <div className="mt-2 text-center">
        <Text type="secondary">
          {filterType === "preset" ? (
            <>
              {period === "week"
                ? "Últimos 7 Días"
                : period === "month"
                  ? "Últimos 30 Días"
                  : period === "quarter"
                    ? "Últimos 90 Días"
                    : period === "year"
                      ? "Último Año"
                      : "Todo el Tiempo"}
            </>
          ) : dateRange && dateRange[0] && dateRange[1] ? (
            <>
              Custom range: {dateRange[0]} to {dateRange[1]}
            </>
          ) : (
            "All data"
          )}
        </Text>
      </div>
    </div>
  );
};

export default FoldersByMonthChart;
